<script>
import { createBranch, checkAdminEmail } from '@/api/branch'

/**
 * 지점 등록 페이지 (HQ 관리자 전용).
 *
 * 섹션 구성:
 *  1. 지점 기본정보
 *  2. 초기 관리자 정보 (필수)
 *  3. 수수료 정책 (선택)
 *
 * 등록 완료 시 생성된 지점의 상세 페이지로 이동한다.
 */
export default {
  name: 'BranchCreatePage',
  data() {
    return {
      saving: false,
      errorMsg: '',
      /** 초기 관리자 이메일 중복 검사 결과 */
      adminEmailCheckMsg: '',
      adminEmailCheckLoading: false,

      branchTypeOptions: [
        { title: '직영점', value: 'DIRECT' },
        { title: '대리점', value: 'AGENCY' },
        { title: '가맹점', value: 'FRANCHISE' },
      ],
      branchStatusOptions: [
        { title: '운영중', value: 'ACTIVE' },
        { title: '일시중지', value: 'INACTIVE' },
        { title: '폐점', value: 'CLOSED' },
      ],

      /** 지점 기본정보 폼 */
      branchForm: {
        branchName: '',
        branchNo: '',
        branchType: 'DIRECT',
        branchStatus: 'ACTIVE',
        zipcode: '',
        address1: '',
        address2: '',
        locationX: '',
        locationY: '',
      },

      /** 초기 관리자 폼 */
      adminForm: {
        adminName: '',
        adminEmail: '',
        adminPassword: '',
        adminPosition: '',
        adminPhone: '',
      },

      /** 수수료 정책 (필수 등록) */
      feeForm: {
        buyFeeRate: '',
        sellFeeRate: '',
        feeStartDate: '',
      },
    }
  },
  methods: {
    /** 초기 관리자 이메일(아이디) 중복 검사 */
    async handleCheckAdminEmail() {
      this.adminEmailCheckMsg = ''
      this.errorMsg = ''
      const email = this.adminForm.adminEmail?.trim()
      if (!email) {
        this.adminEmailCheckMsg = '이메일을 입력한 후 중복 확인해 주세요.'
        return
      }
      this.adminEmailCheckLoading = true
      try {
        const res = await checkAdminEmail(email)
        if (res.data.code === 0) {
          this.adminEmailCheckMsg = '사용 가능한 이메일입니다.'
        } else {
          this.adminEmailCheckMsg = res.data.message || '이미 등록된 이메일입니다.'
        }
      } catch (e) {
        this.adminEmailCheckMsg = e?.response?.data?.message || '중복 검사에 실패했습니다.'
      } finally {
        this.adminEmailCheckLoading = false
      }
    },

    /** 다음 우편번호 서비스 팝업 오픈 */
    openAddressSearch() {
      if (!window.daum?.Postcode) {
        this.errorMsg = '주소 검색 서비스를 불러오는 중입니다. 잠시 후 다시 시도해 주세요.'
        return
      }
      new window.daum.Postcode({
        oncomplete: (data) => {
          // 도로명 주소 우선, 없으면 지번 주소
          const address = data.roadAddress || data.autoRoadAddress || data.jibunAddress
          this.branchForm.zipcode = data.zonecode
          this.branchForm.address1 = address
          // 좌표: Daum Postcode v2에서 x=경도, y=위도 반환
          this.branchForm.locationX = data.x || ''
          this.branchForm.locationY = data.y || ''
        },
      }).open()
    },

    /** 유효성 검사 */
    validate() {
      if (!this.branchForm.branchName?.trim()) {
        this.errorMsg = '지점명은 필수입니다.'
        return false
      }
      if (!this.branchForm.address1?.trim()) {
        this.errorMsg = '주소는 필수입니다. 주소 찾기를 이용해 주세요.'
        return false
      }
      if (!this.adminForm.adminName?.trim()) {
        this.errorMsg = '관리자 이름은 필수입니다.'
        return false
      }
      if (!this.adminForm.adminEmail?.trim()) {
        this.errorMsg = '관리자 이메일은 필수입니다.'
        return false
      }
      if (!this.adminForm.adminPassword?.trim()) {
        this.errorMsg = '관리자 초기 비밀번호는 필수입니다.'
        return false
      }
      if (!this.feeForm.buyFeeRate) {
        this.errorMsg = '매수 수수료율은 필수입니다.'
        return false
      }
      if (!this.feeForm.feeStartDate) {
        this.errorMsg = '수수료 정책 적용 시작일시는 필수입니다.'
        return false
      }
      return true
    },

    /** 등록 처리 */
    async handleSave() {
      this.errorMsg = ''
      if (!this.validate()) return

      this.saving = true
      try {
        const payload = {
          ...this.branchForm,
          branchNo: this.branchForm.branchNo.replace(/-/g, ''),
          ...this.adminForm,
          buyFeeRate: parseFloat(this.feeForm.buyFeeRate),
          sellFeeRate: parseFloat(this.feeForm.sellFeeRate) || 0,
          feeStartDate: this.feeForm.feeStartDate,
        }
        const res = await createBranch(payload)
        if (res.data.code !== 0) {
          this.errorMsg = res.data.message || '등록에 실패했습니다.'
          return
        }
        // 등록된 지점의 상세 페이지로 이동
        const branchCode = res.data.branchCode || res.data.data?.branchCode
        this.$router.push(branchCode ? `/branches/${branchCode}` : '/branches')
      } catch (e) {
        this.errorMsg = e?.response?.data?.message || '서버 오류가 발생했습니다.'
      } finally {
        this.saving = false
      }
    },

    handleCancel() {
      this.$router.push('/branches')
    },

    /** 지점구분 레이블 */
    getBranchTypeLabel(type) {
      const map = { DIRECT: '직영점', AGENCY: '대리점', FRANCHISE: '가맹점' }
      return map[type] || type
    },

    /** 사업자번호 000-00-00000 형식으로 포맷 (숫자만 추출 후 적용) */
    formatBranchNo(value) {
      const digits = (value || '').replace(/\D/g, '').slice(0, 10)
      if (digits.length <= 3) return digits
      if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`
      return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`
    },
  },
}
</script>

<template>
  <div class="branch-create-page">
    <!-- 헤더 -->
    <div class="page-hero mb-7">
      <div class="d-flex align-center ga-1 text-caption text-medium-emphasis mb-4">
        <span class="breadcrumb-link text-primary" @click="$router.push('/branches')">지점관리</span>
        <v-icon size="12">mdi-chevron-right</v-icon>
        <span>신규 등록</span>
      </div>
      <div class="d-flex align-center justify-space-between">
        <div>
          <h1 class="text-h5 font-weight-bold">신규 지점 등록</h1>
          <p class="text-body-2 text-medium-emphasis mt-1">
            지점 정보, 초기 관리자, 수수료 정책을 설정하여 새 지점을 등록합니다.
          </p>
        </div>
      </div>
    </div>

    <!-- 오류 메시지 -->
    <v-alert
      v-if="errorMsg"
      type="error"
      variant="tonal"
      rounded="lg"
      density="compact"
      class="mb-5"
      closable
      @click:close="errorMsg = ''"
    >
      {{ errorMsg }}
    </v-alert>

    <v-row class="branch-create-layout">
      <!-- 왼쪽: 폼 영역 -->
      <v-col cols="12" lg="8">

        <!-- 섹션 1: 지점 기본정보 -->
        <v-card rounded="lg" elevation="0" border class="branch-form-card mb-6">
          <div class="section-header d-flex align-center ga-3 px-6 py-5">
            <div class="section-icon-wrap">
              <v-icon size="18" color="primary">mdi-store-outline</v-icon>
            </div>
            <div>
              <div class="text-subtitle-2 font-weight-bold">지점 기본정보</div>
              <div class="text-caption text-medium-emphasis">지점의 기본 정보를 입력합니다.</div>
            </div>
          </div>
          <v-divider />
          <v-card-text class="section-body">
            <v-row class="branch-form-grid">
              <v-col cols="12" sm="8">
                <v-text-field
                  v-model="branchForm.branchName"
                  label="지점명 *"
                  variant="outlined"
                  density="comfortable"
                  rounded="lg"
                  hide-details="auto"
                  placeholder="예: 강남점"
                />
              </v-col>
              <v-col cols="12" sm="4">
                <v-text-field
                  :model-value="branchForm.branchNo"
                  label="사업자번호"
                  variant="outlined"
                  density="comfortable"
                  rounded="lg"
                  hide-details
                  placeholder="000-00-00000"
                  @update:model-value="branchForm.branchNo = formatBranchNo($event)"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="branchForm.branchType"
                  :items="branchTypeOptions"
                  item-title="title"
                  item-value="value"
                  label="지점구분 *"
                  variant="outlined"
                  density="comfortable"
                  rounded="lg"
                  hide-details
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="branchForm.branchStatus"
                  :items="branchStatusOptions"
                  item-title="title"
                  item-value="value"
                  label="운영상태 *"
                  variant="outlined"
                  density="comfortable"
                  rounded="lg"
                  hide-details
                />
              </v-col>
              <!-- 주소 찾기 -->
              <v-col cols="12">
                <div class="d-flex align-center ga-3">
                  <v-text-field
                    v-model="branchForm.zipcode"
                    label="우편번호"
                    variant="outlined"
                    density="comfortable"
                    rounded="lg"
                    hide-details
                    readonly
                    placeholder="주소 찾기 버튼을 눌러주세요"
                    style="max-width: 160px"
                  />
                  <v-btn
                    color="primary"
                    variant="tonal"
                    rounded="lg"
                    prepend-icon="mdi-map-search-outline"
                    height="48"
                    @click="openAddressSearch"
                  >
                    주소 찾기
                  </v-btn>
                </div>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="branchForm.address1"
                  label="주소 (도로명)"
                  variant="outlined"
                  density="comfortable"
                  rounded="lg"
                  hide-details
                  readonly
                  placeholder="위 주소 찾기 버튼을 이용해주세요"
                />
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="branchForm.address2"
                  label="상세 주소"
                  variant="outlined"
                  density="comfortable"
                  rounded="lg"
                  hide-details
                  placeholder="층, 호수 등 상세 주소 입력"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="branchForm.locationX"
                  label="경도 (X)"
                  variant="outlined"
                  density="comfortable"
                  rounded="lg"
                  hide-details
                  readonly
                  placeholder="주소 선택 시 자동 입력"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="branchForm.locationY"
                  label="위도 (Y)"
                  variant="outlined"
                  density="comfortable"
                  rounded="lg"
                  hide-details
                  readonly
                  placeholder="주소 선택 시 자동 입력"
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- 섹션 2: 초기 관리자 -->
        <v-card rounded="lg" elevation="0" border class="branch-form-card mb-6">
          <div class="section-header d-flex align-center ga-3 px-6 py-5">
            <div class="section-icon-wrap">
              <v-icon size="18" color="indigo">mdi-account-tie-outline</v-icon>
            </div>
            <div>
              <div class="text-subtitle-2 font-weight-bold">초기 관리자 정보</div>
              <div class="text-caption text-medium-emphasis">지점 생성 시 등록될 관리자 계정을 입력합니다.</div>
            </div>
            <v-spacer />
            <v-chip size="x-small" color="error" variant="tonal" label>필수</v-chip>
          </div>
          <v-divider />
          <v-card-text class="section-body">
            <v-row class="branch-form-grid">
              <v-col cols="12" sm="7">
                <v-text-field
                  v-model="adminForm.adminName"
                  label="관리자 이름 *"
                  variant="outlined"
                  density="comfortable"
                  rounded="lg"
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12" sm="5">
                <v-text-field
                  v-model="adminForm.adminPosition"
                  label="직책"
                  variant="outlined"
                  density="comfortable"
                  rounded="lg"
                  hide-details
                  placeholder="예: 점장"
                />
              </v-col>
              <v-col cols="12">
                <div class="d-flex align-center ga-3">
                  <v-text-field
                    v-model="adminForm.adminEmail"
                    label="이메일 (로그인 ID) *"
                    variant="outlined"
                    density="comfortable"
                    rounded="lg"
                    hide-details="auto"
                    type="email"
                    class="flex-grow-1"
                    @blur="adminEmailCheckMsg = ''"
                  />
                  <v-btn
                    variant="tonal"
                    color="indigo"
                    size="small"
                    height="44"
                    :loading="adminEmailCheckLoading"
                    :disabled="!adminForm.adminEmail?.trim()"
                    @click="handleCheckAdminEmail"
                  >
                    중복 확인
                  </v-btn>
                </div>
                <div v-if="adminEmailCheckMsg" class="text-caption mt-1" :class="adminEmailCheckMsg.includes('사용 가능') ? 'text-success' : 'text-error'">
                  {{ adminEmailCheckMsg }}
                </div>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="adminForm.adminPassword"
                  label="초기 비밀번호 *"
                  type="password"
                  variant="outlined"
                  density="comfortable"
                  rounded="lg"
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="adminForm.adminPhone"
                  label="전화번호"
                  variant="outlined"
                  density="comfortable"
                  rounded="lg"
                  hide-details
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- 섹션 3: 수수료 정책 (필수) -->
        <v-card rounded="lg" elevation="0" border class="branch-form-card mb-6">
          <div class="section-header d-flex align-center ga-3 px-6 py-5">
            <div class="section-icon-wrap">
              <v-icon size="18" color="teal">mdi-percent-outline</v-icon>
            </div>
            <div>
              <div class="text-subtitle-2 font-weight-bold">수수료 정책</div>
              <div class="text-caption text-medium-emphasis">지점의 매수·매도 수수료율을 설정합니다.</div>
            </div>
            <v-spacer />
            <v-chip size="x-small" color="error" variant="tonal" label>필수</v-chip>
          </div>
          <v-divider />
          <v-card-text class="section-body">
            <v-row class="branch-form-grid">
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="feeForm.buyFeeRate"
                  label="매수 수수료율 *"
                  variant="outlined"
                  density="comfortable"
                  rounded="lg"
                  type="number"
                  step="0.000001"
                  hide-details="auto"
                  hint="예: 0.003000 = 0.3%"
                  persistent-hint
                  placeholder="0.003000"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="feeForm.sellFeeRate"
                  label="매도 수수료율"
                  variant="outlined"
                  density="comfortable"
                  rounded="lg"
                  type="number"
                  step="0.000001"
                  hide-details="auto"
                  hint="예: 0.005000 = 0.5%"
                  persistent-hint
                  placeholder="0.005000"
                />
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="feeForm.feeStartDate"
                  label="적용 시작일시 *"
                  variant="outlined"
                  density="comfortable"
                  rounded="lg"
                  type="datetime-local"
                  hide-details
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

      </v-col>

      <!-- 오른쪽: 요약 패널 -->
      <v-col cols="12" lg="4">
        <div style="position: sticky; top: 76px">
          <v-card rounded="lg" elevation="0" border>
            <div class="summary-header px-5 py-4">
              <div class="text-subtitle-2 font-weight-bold mb-1">등록 요약</div>
            </div>
            <v-divider />
            <v-list density="compact" class="pa-2">
              <v-list-item class="rounded-lg">
                <template #prepend>
                  <v-icon size="16" color="medium-emphasis" class="mr-2">mdi-store-outline</v-icon>
                </template>
                <v-list-item-title class="text-caption text-medium-emphasis">지점명</v-list-item-title>
                <v-list-item-subtitle class="text-body-2 font-weight-medium text-black">
                  {{ branchForm.branchName || '—' }}
                </v-list-item-subtitle>
              </v-list-item>
              <v-list-item class="rounded-lg">
                <template #prepend>
                  <v-icon size="16" color="medium-emphasis" class="mr-2">mdi-tag-outline</v-icon>
                </template>
                <v-list-item-title class="text-caption text-medium-emphasis">지점구분</v-list-item-title>
                <v-list-item-subtitle class="text-body-2 font-weight-medium text-black">
                  {{ getBranchTypeLabel(branchForm.branchType) }}
                </v-list-item-subtitle>
              </v-list-item>
              <v-list-item class="rounded-lg">
                <template #prepend>
                  <v-icon size="16" color="medium-emphasis" class="mr-2">mdi-account-outline</v-icon>
                </template>
                <v-list-item-title class="text-caption text-medium-emphasis">관리자</v-list-item-title>
                <v-list-item-subtitle class="text-body-2 font-weight-medium text-black">
                  {{ adminForm.adminName || '—' }}
                </v-list-item-subtitle>
              </v-list-item>
              <v-list-item class="rounded-lg">
                <template #prepend>
                  <v-icon size="16" color="medium-emphasis" class="mr-2">mdi-percent</v-icon>
                </template>
                <v-list-item-title class="text-caption text-medium-emphasis">매수 수수료율</v-list-item-title>
                <v-list-item-subtitle class="text-body-2 font-weight-medium text-black">
                  {{ feeForm.buyFeeRate ? ((parseFloat(feeForm.buyFeeRate) * 100).toFixed(4) + '%') : '—' }}
                </v-list-item-subtitle>
              </v-list-item>
            </v-list>
            <v-divider />
            <div class="pa-4 d-flex flex-column ga-2">
              <v-btn
                color="primary"
                variant="flat"
                block
                rounded="lg"
                size="large"
                :loading="saving"
                @click="handleSave"
              >
                <v-icon start>mdi-check</v-icon>
                지점 등록
              </v-btn>
              <v-btn
                variant="outlined"
                block
                rounded="lg"
                :disabled="saving"
                @click="handleCancel"
              >
                취소
              </v-btn>
            </div>
          </v-card>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<style scoped>
/* ── 브레드크럼 ── */
.breadcrumb-link {
  cursor: pointer;
}
.breadcrumb-link:hover {
  text-decoration: underline;
}

.section-icon-wrap {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.04);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.section-header {
  background-color: #fafafa;
  border-radius: inherit;
}

.branch-create-layout {
  align-items: flex-start;
}

.branch-form-card {
  overflow: hidden;
}

.section-body {
  padding: 28px 24px 30px;
}

.branch-form-grid {
  row-gap: 10px;
}

.branch-create-page :deep(.v-field) {
  min-height: 48px;
}

.branch-create-page :deep(.v-field__input) {
  min-height: 48px;
  padding-top: 10px;
  padding-bottom: 10px;
}

.summary-header {
  background-color: #fafafa;
  border-radius: inherit;
}

@media (max-width: 959px) {
  .section-body {
    padding: 22px 18px 24px;
  }
}
</style>
